import { useCallback, useEffect } from 'react';
import logError from '@alltrails/core/utils/logError';
import { useGetResultsBoundingBoxMutation } from '@alltrails/explore-service/redux/exploreServiceApi';
import { updateSearchIsLoading } from '@alltrails/explore-service/redux/exploreServiceSlice';
import { BoundingBoxLocation } from '@alltrails/explore-service/types/ExploreServiceLocation';
import ExploreServicePayload from '@alltrails/explore-service/types/ExploreServicePayload';
import { useAppDispatch, useAppSelector } from '@/utils/redux';

type ExploreQueryArgs = Pick<ExploreServicePayload, 'filters' | 'location' | 'sort'>;

const useExpandSearch = (exploreQueryArgs: ExploreQueryArgs, onNewBoundingBox?: (boundingBox: BoundingBoxLocation) => void) => {
  const dispatch = useAppDispatch();
  const displayMetric = useAppSelector(state => state.context.displayMetric);

  const [getResultsBoundingBox, { isLoading }] = useGetResultsBoundingBoxMutation({ fixedCacheKey: 'shared-expand-search' });

  useEffect(() => {
    dispatch(updateSearchIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const handleResultsBoundingBox = useCallback(
    async (args: Parameters<typeof getResultsBoundingBox>[0]) => {
      await getResultsBoundingBox(args)
        .unwrap()
        .then(response => {
          if (response.boundingBox) {
            onNewBoundingBox?.(response.boundingBox);
          }
        })
        .catch(error => {
          // Without this, a failed request is an unhandled rejection and the zoom out CTA silently does nothing.
          logError(new Error('Failed to expand search bounding box', { cause: error }));
        });
    },
    [getResultsBoundingBox, onNewBoundingBox]
  );
  const expandSearch = useCallback(
    () => handleResultsBoundingBox({ ...exploreQueryArgs, expandSearch: true, localeUnits: displayMetric ? 'metric' : 'imperial' }),
    [handleResultsBoundingBox, exploreQueryArgs, displayMetric]
  );

  return expandSearch;
};

export default useExpandSearch;
